import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

/**
 * Hook for managing query parameters in the URL.
 *
 */
export const useQueryParams = () => {
  const router = useRouter();

  /**
   * Remove a single query param from the URL.
   *
   * @param name
   * @returns void
   */
  const removeQueryParam = useCallback(
    (name: string): void => {
      const { pathname, query } = router;
      const params = new URLSearchParams(query as Record<string, string>);
      if (!params.has(name)) return;

      params.delete(name);
      router.push({ pathname, query: params.toString() }, undefined, { shallow: false });
    },
    [router]
  );

  /**
   * Remove multiple query params from the URL.
   *
   * @param queryNameArray
   * @return void
   */
  const removeQueryParams = useCallback(
    (queryNameArray: string[]): void => {
      const { pathname, query } = router;
      const params = new URLSearchParams(query as Record<string, string>);

      queryNameArray.forEach(queryName => {
        if (!params.has(queryName)) return;
        params.delete(queryName);
      });

      router.push({ pathname, query: params.toString() }, undefined, { shallow: false });
    },
    [router]
  );

  /**
   * Add a single query param to the URL.
   *
   * @param name
   * @param value
   * @return void
   */
  const addQueryParam = useCallback(
    (name: string, value: string): void => {
      const { pathname, query } = router;
      const params = new URLSearchParams(query as Record<string, string>);

      if (params.has(name)) {
        params.delete(name);
      }

      params.append(name, value);
      router.push({ pathname, query: params.toString() }, undefined, { shallow: false });
    },
    [router]
  );

  /**
   * Add multiple query params to the url.
   *
   * @param queryParamArray
   * @return void
   */
  const addQueryParams = useCallback(
    (queryParamArray: { name: string; value: string }[]): void => {
      const { pathname, query } = router;
      const params = new URLSearchParams(query as Record<string, string>);

      queryParamArray.forEach(queryParam => {
        if (params.has(queryParam.name)) {
          params.delete(queryParam.name);
        }

        params.append(queryParam.name, queryParam.value);
      });

      router.push({ pathname, query: params.toString() }, undefined, { shallow: false });
    },
    [router]
  );

  return useMemo(
    () => ({ removeQueryParam, removeQueryParams, addQueryParam, addQueryParams }),
    [addQueryParam, addQueryParams, removeQueryParam, removeQueryParams]
  );
};
